import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import Avatar from "@mui/material/Avatar";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import useWebsocket from "react-use-websocket";

import { env } from "config/env";
import { changeLanguageWithTenant } from "multitenancy/i18n";
import { routeElements } from "routers";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { clearAudienceFilter } from "store/modules/audienceFilter/actions";
import { logout } from "store/modules/auth/actions";
import { updateGlobalConfigurationsAction } from "store/modules/globalConfiguration/actions";
import { setPermissions } from "store/modules/permission/actions";
import {
  selectCustomerProfile,
  setIsAdmin,
  setMenuListAction,
  setProfileNameAction,
} from "store/modules/profile/actions";
import { updateUser } from "store/modules/user/actions";
import { useAppDispatch, useAppSelector } from "store/rootReducer";

import ContainerGlobalDownload from "features/ContainerGlobalUpload";
import { Header } from "features/Header";
import { DownloadAppModal } from "features/Header/DownloadAppModal";
import SelectAudioCamera from "features/SelectAudioCamera";

import { LoadingContainer } from "pages/app/audiences/styles";

import LoadingComponent from "components/LoadingComponent";
import { SystemNotification } from "components/SystemNotification";

import api from "services/api";
import { IGetAllPermissionByCustomerIdAndProfileId } from "services/constants";
import { lifeSignalService } from "services/lifesignal";
import { permissionServices } from "services/permissions";
import { profilesService } from "services/profiles";
import { SystemConfigurationService } from "services/systemConfiguration";
import { ISystemNotificationrBody } from "services/systemNotification";
import { UserCustomerProfileService } from "services/user_customer_profile";

import logo from "assets/Kenta_4.0_Judiciário.png";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_HEARING } from "utils/entities/permission";
import { getPathSegment } from "utils/get";

import {
  getAllNotifications,
  readAllNotifications,
  readNotification,
} from "./constants";

type Props = {
  children: JSX.Element;
  role?: string;
};

export function ProtectedRoute({ children }: Props) {
  const { t } = useTranslation();
  const {
    userCustomerProfile,
    profileName: reduxProfileName,
    menus,
  } = useAppSelector((state) => state.profile);

  const allRoutes = routeElements.props.children.map(
    (route: any) => route.props.path,
  );

  const filteredRoutes = allRoutes
    .filter(
      (r: any) => r.startsWith("/administration") || r.startsWith("/report"),
    )
    .map((r: any) => r.split("/").pop().toUpperCase() || "");

  const { export: exportDownloads } = usePermissions(
    FORMS_HEARING.HEARING_FILE,
  );
  const { globalConfigurations } = useAppSelector(
    (state) => state.configurations,
  );

  const { user } = useAppSelector((state) => state.user);
  const { accessToken } = useAppSelector((state) => state.auth);

  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openPreferencesDrawer, setOpenPreferencesDrawer] =
    useState<boolean>(false);
  const [openDownloadAppModal, setOpenDownloadAppModal] =
    useState<boolean>(false);
  const [menuIndexToShow, setMenuIndexToShow] = useState<number>(-1);
  const [changeAll, setChangeAll] = useState<boolean>(false);
  const [notificationId, setNotificationId] = useState<number>(0);
  const [qntNotifications, setQntNotifications] = useState<number>(0);
  const [listOfNotifications, setListOfNotifications] = useState<
    Array<ISystemNotificationrBody>
  >([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [logoConfiguration, setLogoConfiguration] = useState("");

  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  async function handleGetPermissions({
    customerId,
    profileId,
  }: IGetAllPermissionByCustomerIdAndProfileId) {
    const { data, success } =
      await permissionServices.getAllPermissionByCustomerIdAndProfileId({
        customerId,
        profileId,
      });

    if (success && data && data?.length) {
      dispatch(setPermissions(data ?? []));
      return data ?? [];
    }
  }

  async function GetAllMenuObjectsByUserCustomerProfile(
    customerId: any,
    UserCustomerProfileId: any,
  ) {
    const response =
      await profilesService.GetAllMenuObjectsByUserCustomerProfile(
        customerId,
        UserCustomerProfileId,
      );
    return response;
  }

  useEffect(() => {
    if (changeAll) {
      handleRealAllNotifications();
    }
  }, [changeAll]);

  useEffect(() => {
    if (notificationId !== 0) {
      handleReadNotification();
    }
  }, [notificationId]);

  const match = useMemo(() => {
    if (location.pathname.startsWith("/audiences")) {
      return menus.find(
        (item: { route: string }) => item.route === "/audiences/",
      );
    }

    return menus.find((item: { route: string }) =>
      location.pathname.startsWith(item.route),
    );
  }, [menus, location.pathname]);

  const firstReadableForm = useMemo(() => {
    if (
      match &&
      match.route === "/administration/" &&
      Array.isArray(match.listObjectUserCustomerProfileDTOs)
    ) {
      const prof = match.listObjectUserCustomerProfileDTOs.find(
        (p: {
          listObjectUserProfileActionDTOs: { action: string }[];
          name: string;
        }) =>
          p.listObjectUserProfileActionDTOs.some(
            (act: { action: string }) =>
              act.action === "Read" &&
              filteredRoutes.includes(p.name?.toUpperCase()),
          ),
      );
      return prof?.name ?? null;
    }

    if (
      match &&
      match.route === "/report/" &&
      Array.isArray(match.listObjectUserCustomerProfileDTOs)
    ) {
      const prof = match.listObjectUserCustomerProfileDTOs.find(
        (p: {
          listObjectUserProfileActionDTOs: { action: string }[];
          name: string;
        }) =>
          p.listObjectUserProfileActionDTOs.some(
            (act: { action: string }) =>
              act.action === "Read" &&
              filteredRoutes.includes(p.name?.toUpperCase()),
          ),
      );
      return prof?.name ?? null;
    }
    return null;
  }, [match]);

  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setMenuIndexToShow(-1);
      return;
    }

    if (match) {
      setMenuIndexToShow(match.index);
      if (location.pathname === match.route && firstReadableForm) {
        navigate(`${match.route}${firstReadableForm}`);
      }
    } else {
      setMenuIndexToShow(-1);
    }
  }, [location?.pathname, match, firstReadableForm]);

  const pagePath = useMemo(
    () => getPathSegment(location.pathname, 1),
    [location.pathname],
  );

  const { read: canReadPage } = usePermissions(pagePath ?? "");

  useEffect(() => {
    if (pagePath && !canReadPage && firstReadableForm) {
      navigate(`${match.route}${firstReadableForm}`);
    }
  }, [pagePath, canReadPage]);

  const { addToast } = useToast();

  const [lifeSignal, setLifeSignal] = useState("True");

  useEffect(() => {
    if (lifeSignal === "False") {
      addToast({
        type: "warning",
        title: t("common.accessFinished"),
        description: t("auth.sessionExpired"),
      });
      dispatch(logout());
    }
  }, [lifeSignal]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      (async () => {
        if (user && user.hash) {
          const { data, success } = await lifeSignalService.put(user.hash);
          if (success) {
            setLifeSignal(data);
          } else {
            setLifeSignal("False");
          }
        }
      })();
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const checkUserAccess = useCallback(async () => {
    if (!user?.id || !accessToken) return;

    const currentPlaceId = userCustomerProfile?.placeId;

    try {
      const response = await UserCustomerProfileService.getAllByUserCustomer(
        accessToken,
        user.id,
      );

      if (!response.status || !response.data) {
        return;
      }

      const profiles = response.data;

      const allPlaces = profiles.flatMap(
        (profile: { places?: { id: number }[] }) =>
          Array.isArray(profile.places) ? profile.places : [],
      );

      const hasAnyPlace = allPlaces.length > 0;
      const currentPlaceStillExists =
        currentPlaceId != null &&
        allPlaces.some((place: { id: number }) => place.id === currentPlaceId);

      if (!hasAnyPlace) {
        addToast({
          type: "warning",
          title: t("common.accessRemoved"),
          description: t("auth.accessRemovedDescription"),
        });
        dispatch(logout());
        return;
      }

      if (!currentPlaceStillExists) {
        addToast({
          type: "warning",
          title: t("auth.courtAccessRemoved"),
          description: t("auth.courtAccessRemovedDescription"),
        });
        dispatch(logout());
      }
    } catch {
      // Silent fail - avoid disconnecting user on network errors
    }
  }, [user?.id, accessToken, userCustomerProfile?.placeId, addToast, dispatch]);

  useEffect(() => {
    const intervalId = setInterval(checkUserAccess, 120000);

    return () => clearInterval(intervalId);
  }, [checkUserAccess]);

  const WS_URL_NOTIFICATION = useMemo(
    () =>
      `${import.meta.env.VITE_APP_WSS_BASE_URL}/notifications?token=${accessToken}`,
    [accessToken],
  );
  const { sendMessage: sendMessageNotification } = useWebsocket(
    WS_URL_NOTIFICATION,
    {
      onMessage: (event: MessageEvent<any>) => {
        if (event.data !== "-1") setQntNotifications(Number(event.data));
      },
      onOpen: () => {},
      onClose: () => {},
      shouldReconnect: () => true,
      reconnectInterval: 1000,
    },
  );

  useEffect(() => {
    if (LogOperation.getUserId()) {
      sendMessageNotification(String(LogOperation.getUserId()));
    }
  }, []);

  useEffect(() => {
    const intervalIdNotification = setInterval(() => {
      (async () => {
        if (user) {
          sendMessageNotification(user?.id);
        }
      })();
    }, 5000);

    return () => clearInterval(intervalIdNotification);
  }, []);

  const changeProfile = async (newProfile: any) => {
    dispatch(selectCustomerProfile(newProfile));
    dispatch(setIsAdmin(newProfile.profileType === 2));
    if (newProfile && newProfile.profileName) {
      if (user) {
        dispatch(clearAudienceFilter());
        dispatch(updateUser(user));
        await handleGetPermissions({
          profileId: newProfile?.profileId,
          customerId: user?.customerId,
        });
        const profileMenu = await GetAllMenuObjectsByUserCustomerProfile(
          user?.customerId,
          newProfile?.profileId,
        );
        const menuKeyMap: Record<string, string> = {
          Hearing: "menu.audiences",
          Administration: "menu.administration",
          Report: "menu.reports",
          DashboardMaster: "menu.dashboard",
        };
        const profileMenuRenamed = profileMenu.map(
          ({ url, description, name, menu, ...rest }: any, index: any) => ({
            ...rest,
            route: url,
            label: menuKeyMap[name] || menu.toUpperCase(),
            location: description,
            show: true,
            index,
          }),
        );
        dispatch(setMenuListAction(profileMenuRenamed));
        navigate(
          newProfile.profileName === "Master" ? "/dashboard/" : "/audiences/",
        );

        let match;
        if (location.pathname.startsWith("/audiences/")) {
          match = profileMenuRenamed.find(
            (item: { route: string }) => item.route === "/audiences/",
          );
        } else {
          match = profileMenuRenamed.find((item: { route: string }) =>
            location.pathname.startsWith(item.route),
          );
        }
        if (match) {
          setMenuIndexToShow(match.index);
        } else {
          setMenuIndexToShow(-1);
        }
      }
      dispatch(setProfileNameAction(newProfile.profileName));
    }
  };

  useEffect(() => {
    (async () => {
      const customerId = user?.customerId;
      if (customerId && !globalConfigurations?.length) {
        const { data } =
          await SystemConfigurationService.getAllByCustomer(customerId);

        dispatch(updateGlobalConfigurationsAction(data));
      }
    })();
  }, []);

  useEffect(() => {
    if (Array.isArray(globalConfigurations) && globalConfigurations.length) {
      let hasAppLanguage = false;
      globalConfigurations?.forEach((element: any) => {
        if (element && element.name === "LogoCustomer") {
          setLogoConfiguration(element.value);
        }
        if (element && element.name === "ApplicationLanguage") {
          localStorage.setItem("appLanguage", element.value);
          changeLanguageWithTenant(element.value);
          hasAppLanguage = true;
        }
      });
      if (!hasAppLanguage && !env.LANGUAGE_SELECTOR_ENABLED) {
        changeLanguageWithTenant(env.DEFAULT_LANGUAGE);
      }
    }
  }, [globalConfigurations]);

  async function getNotifications() {
    const notificationAutoConfig = Array.isArray(globalConfigurations)
      ? globalConfigurations.find(
          (config: any) => config.name === "NotificationAuto",
        )
      : [];

    const notificationAutoValue = notificationAutoConfig?.value;

    if (notificationAutoValue === "0") {
      const list = await getAllNotifications(api, LogOperation.getUserId());

      for (const notification of list) {
        await readNotification(api, notification.id);
      }

      setListOfNotifications([]);
      setQntNotifications(0);
      return;
    }

    const list = await getAllNotifications(api, LogOperation.getUserId());

    setListOfNotifications(list);
    setQntNotifications(list.length);
  }

  const filteredQntNotifications = (() => {
    const notificationAutoConfig = Array.isArray(globalConfigurations)
      ? globalConfigurations.find(
          (config: any) => config.name === "NotificationAuto",
        )
      : [];
    return notificationAutoConfig?.value === "1" ? qntNotifications : 0;
  })();

  const onClickNotifications = async () => {
    await getNotifications();
    setOpenDrawer(true);
  };

  const handleReadNotification = async () => {
    const response = await readNotification(api, notificationId);
    if (response.status === 200) {
      const filteredList = listOfNotifications?.filter(
        (item) => item?.id !== notificationId,
      );
      setListOfNotifications(filteredList);
    }
    return false;
  };

  const handleRealAllNotifications = async () => {
    setLoading(true);

    try {
      const response = await readAllNotifications(api, listOfNotifications);

      if (response?.status === 200) {
        setListOfNotifications([]);
        setChangeAll(false);
        setQntNotifications(0);
      }
    } catch (error: any) {
      console.error("Erro ao marcar notificações como lidas:", error);
    } finally {
      setLoading(false);
    }
  };

  if (children && !user) {
    return <Navigate to="/" />;
  }

  return (
    <Fragment>
      <Header
        logo={logoConfiguration || logo}
        icons={[]}
        menuIndexToShow={menuIndexToShow}
        menus={menus}
        avatar={<Avatar src="https://mui.com/static/images/avatar/1.jpg" />}
        api={api}
        setOpenPreferencesDrawer={setOpenPreferencesDrawer}
        customerId={user?.customerId ?? 0}
        selectedProfile={userCustomerProfile}
        profileName={reduxProfileName}
        setSelectedProfile={changeProfile}
        indexHeaderMenu={0}
        onClickNotifications={onClickNotifications}
        onClickDownloadApp={() => setOpenDownloadAppModal(true)}
        qntNotifications={filteredQntNotifications}
      />
      <SystemNotification
        title={t("notifications.title")}
        emptyMessage={t("notifications.none")}
        setOpenDrawer={setOpenDrawer}
        openDrawer={openDrawer}
        labelReadAll={t("notifications.markAllAsRead")}
        changeAll={changeAll}
        setChangeAll={setChangeAll}
        listNotifications={listOfNotifications}
        setNotificationId={setNotificationId}
      />
      {children}
      {loading ? (
        <LoadingContainer>
          <LoadingComponent loading={loading} />
        </LoadingContainer>
      ) : null}
      {openDownloadAppModal && exportDownloads ? (
        <DownloadAppModal
          isOpenDownloadsModal={openDownloadAppModal}
          setIsOpenDownloadsModal={() =>
            setOpenDownloadAppModal(!openDownloadAppModal)
          }
        />
      ) : null}
      <ContainerGlobalDownload />
      <SelectAudioCamera
        setOpen={setOpenPreferencesDrawer}
        open={openPreferencesDrawer}
      />
    </Fragment>
  );
}
